/**
 * WorkDocListStyles — Numbered list formats + bullet library.
 */
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { List, ListOrdered, ListChecks } from "lucide-react";

const NUMBERED_STYLES = [
  { label: "1. 2. 3.", value: "decimal", icon: "1." },
  { label: "a. b. c.", value: "lower-alpha", icon: "a." },
  { label: "A. B. C.", value: "upper-alpha", icon: "A." },
  { label: "i. ii. iii.", value: "lower-roman", icon: "i." },
  { label: "I. II. III.", value: "upper-roman", icon: "I." },
];

const BULLET_STYLES = [
  { label: "Disc", value: "disc", icon: "●" },
  { label: "Circle", value: "circle", icon: "○" },
  { label: "Square", value: "square", icon: "■" },
];

export default function WorkDocListStyles({ editor }) {
  if (!editor) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1 border border-gray-700 hover:bg-gray-800 text-gray-400">
          <ListOrdered className="w-4 h-4" />
          <span className="hidden sm:inline">List</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 bg-gray-900 border-gray-700" side="bottom" align="start">
        <div className="space-y-2">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 px-1">Numbered Lists</p>
            <div className="grid grid-cols-1 gap-0.5">
              {NUMBERED_STYLES.map((style) => (
                <button
                  key={style.value}
                  className="flex items-center gap-3 px-2 py-1.5 text-sm text-gray-300 hover:bg-gray-800 rounded"
                  onClick={() => {
                    editor.chain().focus().toggleOrderedList().run();
                    // Note: TipTap orderedList doesn't natively support list-style-type attribute
                    // This sets it via CSS on the selected list
                    const { from, to } = editor.state.selection;
                    if (from !== undefined) {
                      editor.chain().focus().setTextSelection({ from, to }).run();
                    }
                  }}
                >
                  <span className="w-6 text-xs font-mono text-gray-500">{style.icon}</span>
                  <span>{style.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="h-px bg-gray-700" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 px-1">Bullet Styles</p>
            <div className="grid grid-cols-3 gap-1">
              {BULLET_STYLES.map((style) => (
                <button
                  key={style.value}
                  className="flex flex-col items-center gap-1 px-2 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded"
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                  <span className="text-lg">{style.icon}</span>
                  <span className="text-[10px] text-gray-500">{style.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="h-px bg-gray-700" />
          <button
            className="flex items-center gap-3 px-2 py-1.5 text-sm text-gray-300 hover:bg-gray-800 rounded w-full"
            onClick={() => editor.chain().focus().toggleTaskList().run()}
          >
            <ListChecks className="w-4 h-4 text-gray-500" />
            <span>Task List (Checkboxes)</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
